import { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { supabase } from '../../lib/supabase'

// Mappa passnamn → kategori
function tolkKategori(passNamn) {
  const u = (passNamn || '').toUpperCase().trim()
  if (!u) return 'ordinarie'
  // Sjukdom med pass: SjF, SjD osv (måste kollas FÖRE generella S-prefixet,
  // annars fångas "SJF" av S-grenen nedan och blir felaktigt semester)
  if (u.startsWith('SJ') && u.length > 2) return 'sjukdom'
  if (u === 'SJK' || u === 'SJUK' || u === 'SJUKDOM') return 'sjukdom'
  // Semester med pass: SF, SD osv
  if (u.startsWith('S') && u.length > 1 && !['SEM','SEMESTER'].includes(u)) return 'semester'
  if (u === 'SEM' || u === 'SEMESTER') return 'semester'
  // Betald ledig dag: LF, LD, L osv
  if (u === 'L' || (u.startsWith('L') && u.length > 1)) return 'ledig'
  if (u === 'LED' || u === 'LEDIG') return 'ledig'
  // Veckovila
  if (u === 'V') return 'ledig'
  // Övertid: ÖT framför ett pass
  if (u.startsWith('ÖT') || u.startsWith('OT') || u.startsWith('ÖT ')) return 'overtid'
  // Beredskap
  if (u === 'BER' || u === 'BEREDSKAP') return 'beredskap'
  // Boka dag
  if (u === 'BOK' || u === 'BOKA' || u === 'BOKAT') return 'boka_dag'
  // Passbyte
  if (u === 'BYT' || u === 'BYTE' || u === 'PASSBYTE') return 'passbyte'
  return 'ordinarie'
}

// Specialpass som ska importeras även om "tomma" (ledig dag med färg)
function skaImporteras(passNamn) {
  const u = (passNamn || '').toUpperCase().trim()
  if (!u) return false
  return true // Importera alla celler med värde
}

// Hämta baspassnamn (SF → F, SD → D osv)
// Läser fyllnadsfärgen för en cell (tar ws som parameter så den fungerar
// oavsett vilken inläsningsfunktion som anropar den).
function hamtaCellFarg(ws, radIdx, kolIdx) {
  try {
    const cellAdress = XLSX.utils.encode_cell({ r: radIdx, c: kolIdx })
    const cell = ws[cellAdress]
    if (!cell) return null

    const s = cell.s
    if (!s) return null

    let rgb = null
    if (s.fgColor && (s.fgColor.rgb || s.fgColor.argb)) {
      rgb = s.fgColor.rgb || s.fgColor.argb
    } else if (s.fill && s.fill.fgColor && (s.fill.fgColor.rgb || s.fill.fgColor.argb)) {
      rgb = s.fill.fgColor.rgb || s.fill.fgColor.argb
    } else if (s.bgColor && (s.bgColor.rgb || s.bgColor.argb)) {
      rgb = s.bgColor.rgb || s.bgColor.argb
    } else if (s.patternFill && s.patternFill.fgColor) {
      rgb = s.patternFill.fgColor.rgb || s.patternFill.fgColor.argb
    }

    if (!rgb) return null
    const clean = rgb.replace('#','')
    return clean.length === 8 ? clean.slice(2).toUpperCase() : clean.toUpperCase()
  } catch(e) {
    return null
  }
}

// Mappa RGB-färg → kategori baserat på exakta Excel-färger. Samma regler
// används av alla avdelningar (RN, LN MMÖ, LN NKP och DISP).
function fargTillKategori(rgb) {
  if (!rgb) return null
  const hex = rgb.length === 8 ? rgb.slice(2) : rgb
  const r = parseInt(hex.slice(0,2), 16)
  const g = parseInt(hex.slice(2,4), 16)
  const b = parseInt(hex.slice(4,6), 16)

  if (r > 240 && g > 240 && b > 240) return null
  if (r < 15 && g < 15 && b < 15) return null

  if (r > 180 && g < 80 && b < 80) return 'beredskap'
  if (r > 200 && g > 200 && b < 80) return 'semester'
  if (r < 80 && g > 140 && b > 180) return 'boka_dag'
  if (r > 180 && g > 140 && b > 140 && r > g && r > b) return 'passbyte'
  if (r > 200 && g > 80 && g < 160 && b < 80) return 'passandring'
  if (r > 100 && r < 200 && g < 100 && b > 150) return 'sjukdom'
  if (g > 180 && r < 150 && b < 150) return 'beviljad_semester'

  return null
}

function basPassNamn(passNamn) {
  const u = (passNamn || '').toUpperCase().trim()
  // ÖT-prefix: ÖT F1→F1, ÖT E→E osv
  if (u.startsWith('ÖT ') || u.startsWith('OT ')) return u.slice(3).trim()
  if (u.startsWith('ÖT') && u.length > 2) return u.slice(2).trim()
  // Sj-prefix: SjF→F, SjD→D osv (måste kollas FÖRE S-prefixet nedan)
  if (u.startsWith('SJ') && u.length > 2) return u.slice(2)
  // S-prefix: SF→F, SD→D osv (men inte SEM)
  if (u.startsWith('S') && u.length > 1 && !['SEM','SEMESTER'].includes(u)) {
    return u.slice(1)
  }
  // L-prefix: LF→F, LD→D osv (men inte LED, LEDIG)
  if (u.startsWith('L') && u.length > 1 && !['LED','LEDIG'].includes(u)) {
    return u.slice(1)
  }
  return u
}

// Fix timezone-problem: tolka datum som lokal tid, inte UTC
function normaliseraDatum(str) {
  if (!str) return null
  // Om det redan är YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  // Om det är ett Date-objekt eller ISO-sträng från XLSX
  if (str instanceof Date) {
    const y = str.getFullYear()
    const m = String(str.getMonth() + 1).padStart(2, '0')
    const d = String(str.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  // Hantera XLSX-datumsträngar (kan vara "Mon Jun 15 2025 00:00:00 GMT+0200")
  const parsed = new Date(str)
  if (!isNaN(parsed)) {
    // Använd lokal tid för att undvika timezone-shift
    const y = parsed.getFullYear()
    const m = String(parsed.getMonth() + 1).padStart(2, '0')
    const d = String(parsed.getDate()).padStart(2, '0')
    // Kolla om det ser ut som om timezone dragit av en dag
    const iso = parsed.toISOString().split('T')[0]
    const local = `${y}-${m}-${d}`
    return local
  }
  // DD/MM/YYYY eller DD-MM-YYYY
  const delar = str.split(/[/\-.]/)
  if (delar.length === 3) {
    if (delar[0].length === 4) return `${delar[0]}-${delar[1].padStart(2,'0')}-${delar[2].padStart(2,'0')}`
    return `${delar[2]}-${delar[1].padStart(2,'0')}-${delar[0].padStart(2,'0')}`
  }
  return str
}

function uppskattaTimmar(passNamn) {
  const bas = basPassNamn(passNamn)
  const timmar = {
    'F': 8.5, 'F1': 8.5, 'D1': 8.5, 'D3': 8.5,
    'D': 8.0, 'V': 8.0,
    'D2': 8.5, 'E': 8.5, 'E1': 8.5, 'N': 8.5,
    'X': 0, 'SX': 0, // SX sätts dynamiskt efter import
  }
  return timmar[bas] || 0
}

export function LaddaUppPage() {
  const [period, setPeriod] = useState(10)
  const [valdAvdelning, setValdAvdelning] = useState('LN MMÖ')
  const AVDELNINGAR = ['RN', 'LN MMÖ', 'LN NKP', 'DISP']
  // Mall: avdelning + startdatum + antal veckor → Excel med datum och alla aktiva medarbetare ifyllda
  const [mallAvd, setMallAvd] = useState('LN MMÖ')
  const [mallStart, setMallStart] = useState('')
  const [mallVeckor, setMallVeckor] = useState(10)
  const [mallFel, setMallFel] = useState('')
  const [mallHamtar, setMallHamtar] = useState(false)

  // Den vanliga generella mallen (exempeldata), för när inget startdatum är valt
  function laddaNerGenerellMall() {
    const wb = XLSX.utils.book_new()
    const headers = ['Namn', '2026-06-15', '2026-06-16', '2026-06-17', '2026-06-18', '2026-06-19', '2026-06-20', '2026-06-21']
    const exempelData = [
      ['Anna Karlsson', 'F', 'D', 'SF', 'E', 'N', '', ''],
      ['Erik Lindqvist', 'D', '', 'F', 'BER', '', 'SJK', 'D'],
    ]
    const ws = XLSX.utils.aoa_to_sheet([headers, ...exempelData])
    ws['!cols'] = [{ wch: 20 }, ...headers.slice(1).map(() => ({ wch: 8 }))]
    XLSX.utils.book_append_sheet(wb, ws, 'Schema')
    XLSX.writeFile(wb, 'eon_schema_mall.xlsx')
  }

  async function laddaNerMall() {
    if (!mallStart) { setMallFel(''); laddaNerGenerellMall(); return }
    setMallFel('')
    setMallHamtar(true)
    try {
      const { data: meds, error } = await supabase
        .from('medarbetare')
        .select('fornamn, efternamn')
        .eq('avdelning', mallAvd)
        .eq('ar_aktiv', true)
        .order('sort_order', { ascending: true, nullsFirst: false })
      if (error) throw error
      if (!meds || meds.length === 0) {
        setMallFel(`Inga aktiva medarbetare hittades i ${mallAvd}.`)
        return
      }
      // Datum i rad 1 (ÅÅÅÅ-MM-DD, lokal tid), ett per dag
      const [a, m, d] = mallStart.split('-').map(Number)
      const headers = ['Namn']
      for (let i = 0; i < mallVeckor * 7; i++) {
        const dag = new Date(a, m - 1, d + i)
        headers.push(`${dag.getFullYear()}-${String(dag.getMonth() + 1).padStart(2, '0')}-${String(dag.getDate()).padStart(2, '0')}`)
      }
      const rader = meds.map(x => [`${x.fornamn} ${x.efternamn}`, ...headers.slice(1).map(() => '')])
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rader])
      ws['!cols'] = [{ wch: 22 }, ...headers.slice(1).map(() => ({ wch: 11 }))]
      XLSX.utils.book_append_sheet(wb, ws, 'Schema')
      XLSX.writeFile(wb, `eon_schema_mall_${mallAvd.replace(/\s+/g, '_')}_${mallStart}.xlsx`)
    } catch (err) {
      console.error('Mall:', err)
      setMallFel('Kunde inte skapa mallen: ' + (err?.message || 'okänt fel'))
    } finally {
      setMallHamtar(false)
    }
  }
  const [preview, setPreview] = useState(null)
  const [importerar, setImporterar] = useState(false)
  const [importStatus, setImportStatus] = useState('')
  const [klar, setKlar] = useState(false)
  const [ersattBefintliga, setErsattBefintliga] = useState(false)
  const [dialog, setDialog] = useState(null) // { titel, text, rader, okText, avbrytText, resolve }

  // Snygg dialogruta istället för webbläsarens alert/confirm. Returnerar true/false.
  function visaDialog({ titel, text, rader, okText = 'OK', avbrytText = null }) {
    return new Promise(resolve => {
      setDialog({ titel, text, rader, okText, avbrytText, resolve })
    })
  }
  function stangDialog(svar) {
    if (dialog) dialog.resolve(svar)
    setDialog(null)
  }
  const [kandaPasstyper, setKandaPasstyper] = useState(null)
  const [schemaTypPerNamn, setSchemaTypPerNamn] = useState({})

  useEffect(() => {
    supabase.from('passtyper').select('namn').then(({ data }) => {
      setKandaPasstyper(new Set((data || []).map(p => p.namn.toUpperCase())))
    })
  }, [])

  // Hämta schematyp per person i den valda avdelningen, så förhandsvisningen
  // (innan importen faktiskt körs) kan visa rätt timkrav även för 40h-personer
  // i perioder som inte är exakt 8 veckor.
  useEffect(() => {
    if (!valdAvdelning) return
    supabase.from('medarbetare').select('fornamn, efternamn, schema_typ').eq('avdelning', valdAvdelning).then(({ data }) => {
      const karta = {}
      for (const m of data || []) {
        karta[`${m.fornamn} ${m.efternamn}`.trim().toLowerCase()] = m.schema_typ
      }
      setSchemaTypPerNamn(karta)
    })
  }, [valdAvdelning])

  function handleFil(e) {
    const fil = e.target.files?.[0]
    if (!fil) return
    lasExcel(fil)
  }

  function handleDrop(e) {
    e.preventDefault()
    const fil = e.dataTransfer.files?.[0]
    if (fil) lasExcel(fil)
  }

  function lasExcel(fil, sheetName) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result)
      const wb = XLSX.read(data, { type: 'array', cellDates: true, cellStyles: true, cellNF: true, cellHTML: false })
      const ws = wb.Sheets[sheetName || wb.SheetNames[0]]
      const rader = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' })

      const headers = rader[0]
      const dagHeaders = headers.slice(1)

      // Konvertera datumhuvuden
      const dagDatum = dagHeaders.map((h) => {
        if (!h) return null
        if (h instanceof Date) return normaliseraDatum(h)
        if (typeof h === 'number') {
          const d = XLSX.SSF.parse_date_code(h)
          if (d) return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`
        }
        return normaliseraDatum(h.toString())
      })

      // Hjälpfunktion: läs cellfärg från worksheet
      const medarbetareData = []
      const okandaKoder = [] // { namn, datum, kod } - upptäckta koder som inte finns i passtyper

      const GENERISKA_KATEGORIORD = new Set([
        'V','L','LED','LEDIG','SEM','SEMESTER','BER','BEREDSKAP',
        'SJK','SJUK','SJUKDOM','BOK','BOKA','BOKAT','BYT','BYTE','PASSBYTE'
      ])
      function arKandKod(kod) {
        if (!kandaPasstyper) return true // om listan inte hunnit laddas, blockera inte importen
        if (GENERISKA_KATEGORIORD.has(kod)) return true
        const bas = basPassNamn(kod)
        return kandaPasstyper.has(kod) || kandaPasstyper.has(bas)
      }

      for (let i = 1; i < rader.length; i++) {
        const rad = rader[i]
        if (!rad[0]) continue

        const namn = rad[0].toString().trim()
        const passRader = []
        let totalTimmar = 0

        for (let j = 1; j < headers.length; j++) {
          const datum = dagDatum[j - 1]
          if (!datum) continue

          // Läs cell direkt från ws-objektet (fångar även tomma celler med färg)
          const cellAdress = XLSX.utils.encode_cell({ r: i, c: j })
          const wsCell = ws[cellAdress]
          const rawVal = wsCell ? wsCell.v : ''
          let cellVal = (rawVal || '').toString().trim().toUpperCase()
          // Säkerhetsåtgärd: förhindra formelinjektion. Om cellen börjar med
          // =, +, - eller @ skulle den kunna tolkas som en körbar formel om
          // datan senare öppnas i en exporterad Excel-fil.
          if (/^[=+\-@]/.test(cellVal)) cellVal = "'" + cellVal

          // Läs cellfärg
          const farg = hamtaCellFarg(ws, i, j)
          const fargKategori = fargTillKategori(farg)

          if (cellVal) {
            // Okänd kod - hoppas över helt (importeras inte), flaggas i varningslistan
            if (!arKandKod(cellVal)) {
              okandaKoder.push({ namn, datum, kod: cellVal })
              continue
            }
            // Cell har värde
            const namnKategori = tolkKategori(cellVal)
            // Beredskap/bokad dag är numera egna, fristående flaggor (som ATF) -
            // de ska INTE skriva över hela cellens grundkategori/färg längre.
            const harBeredskap = fargKategori === 'beredskap'
            const harBokaDag = fargKategori === 'boka_dag'
            const kategori = (fargKategori && !harBeredskap && !harBokaDag) ? fargKategori : namnKategori
            passRader.push({
              datum,
              passNamn: cellVal,
              kategori,
              basPass: basPassNamn(cellVal),
              harBeredskap,
              harBokaDag
            })
            totalTimmar += uppskattaTimmar(cellVal)
          } else if (fargKategori) {
            // Tom cell med färg – importera med kategori från färgen (om det
            // inte är beredskap/bokad dag, som blir egna flaggor istället)
            const harBeredskap = fargKategori === 'beredskap'
            const harBokaDag = fargKategori === 'boka_dag'
            passRader.push({
              datum,
              passNamn: '',
              kategori: (harBeredskap || harBokaDag) ? 'ordinarie' : fargKategori,
              basPass: '',
              harBeredskap,
              harBokaDag
            })
          }
          // Helt tom cell utan färg = ledig dag, importeras inte
        }

        const personSchemaTyp = schemaTypPerNamn[namn.trim().toLowerCase()]
        const kravTimmar = (period === 8 || personSchemaTyp === '40h') ? period * 40 : period * 34.5
        medarbetareData.push({
          namn,
          pass: passRader,
          totalTimmar: parseFloat(totalTimmar.toFixed(1)),
          kravTimmar,
          ok: Math.abs(totalTimmar - kravTimmar) < 1
        })
      }

      const unikaPassNamn = [...new Set(
        medarbetareData.flatMap(m => m.pass.map(p => p.passNamn))
      )].filter(Boolean)

      const allaDatum = dagDatum.filter(Boolean)
      setPreview({
        filnamn: fil.name,
        medarbetare: medarbetareData,
        dagar: allaDatum.length,
        veckor: period,
        passNamn: unikaPassNamn,
        forstaDatum: allaDatum[0],
        sistaDatum: allaDatum[allaDatum.length - 1],
        okandaKoder,
        _rawData: medarbetareData // spara för att kunna räkna om vid periodbyte
      })
    }
    reader.readAsArrayBuffer(fil)
  }

    async function importera() {
    if (!preview) return
    setImporterar(true)

    try {
      setImportStatus('Hämtar medarbetare...')
      const { data: allaMedarbetare } = await supabase.from('medarbetare').select('id, fornamn, efternamn, avdelning, schema_typ')
      const { data: allaPasstyper } = await supabase.from('passtyper').select('id, namn, arbetstimmar, ar_x_dag')
      const { data: { user } } = await supabase.auth.getUser()

      // Bygg upp alla pass i en array för batch-insert
      const allaBatchPass = []
      const medarbetareMedX = {} // medarbetare_id -> sort_order

      for (let medIdx = 0; medIdx < preview.medarbetare.length; medIdx++) {
        const med = preview.medarbetare[medIdx]
        let matchad
        if (med.medarbetareId) {
          // Redan säkert matchad vid inläsningen (DISP-läget matchar bara
          // mot DISP:s egna medarbetare via förnamn) - matcha inte om via
          // det generella, bredare namn-sättet, det skulle kunna bli fel.
          matchad = allaMedarbetare.find(m => m.id === med.medarbetareId)
        } else {
          // Normalisera namn - ta bort snedstreck och extra mellanslag
          const normalNamn = med.namn.toLowerCase().replace(/[\/\\]/g, ' ').replace(/\s+/g, ' ').trim()
          matchad = allaMedarbetare.find(m => {
            const fornamn = m.fornamn.toLowerCase()
            const efternamn = m.efternamn.toLowerCase()
            return normalNamn.includes(fornamn) && normalNamn.includes(efternamn)
          })
        }
        console.log('Försöker matcha:', med.namn, '→', matchad ? `${matchad.fornamn} ${matchad.efternamn}` : 'INGEN MATCH')
        if (!matchad) {
          console.warn('Hittade inte:', med.namn)
          continue
        }

        medarbetareMedX[matchad.id] = { sort_order: medIdx, schema_typ: matchad.schema_typ, avdelning: matchad.avdelning }

        for (const p of med.pass) {
          // Matcha mot baspassnamnet (LF→F, SF→F, SE1→E1 osv)
          const passtyp = allaPasstyper.find(pt =>
            pt.namn.toUpperCase() === p.basPass.toUpperCase() ||
            pt.namn.toUpperCase() === p.passNamn.toUpperCase()
          )
          // Hämta timmar från passtyp eller uppskatta från baspassnamnet
          // Beräkna arbetstimmar:
          // 1. Tom cell = 0h
          // 2. Exakt match i passtyper = använd den
          // 3. S/L-prefix: slå upp baspasset i DB (SF→F, SD1→D1 osv)
          // 4. Fallback: uppskattning från hårdkodad lista
          let arbetstimmar = 0
          if (!p.passNamn) {
            arbetstimmar = 0
          } else if (p.kategori === 'overtid') {
            // Övertid räknas INTE i timtotalen
            arbetstimmar = 0
          } else if (passtyp) {
            arbetstimmar = passtyp.arbetstimmar || 0
          } else {
            // Slå upp baspasset i DB (fungerar för alla framtida pass också!)
            const basPasstyp = allaPasstyper.find(pt =>
              pt.namn.toUpperCase() === p.basPass.toUpperCase()
            )
            if (basPasstyp) {
              arbetstimmar = basPasstyp.arbetstimmar || 0
            } else {
              // Sista fallback
              arbetstimmar = uppskattaTimmar(p.passNamn)
            }
          }

          allaBatchPass.push({
            medarbetare_id: matchad.id,
            datum: p.datum,
            passtyp_id: passtyp?.id || null,
            passtyp_namn: p.passNamn || '',
            kategori: p.kategori,
            ar_x_dag: passtyp?.ar_x_dag || false,
            arbetstimmar,
            notering: p.notering || null,
            visa_notering_i_schema: !!p.notering,
            har_beredskap: !!p.harBeredskap,
            har_boka_dag: !!p.harBokaDag
          })
        }
      }

      const unikaMedIds = [...new Set(allaBatchPass.map(p => p.medarbetare_id))]

      // ERSÄTT-LÄGE: använd den befintliga perioden (skapa ingen ny) och
      // bekräfta vad som raderas INNAN något tas bort.
      const befintligPeriodPerAvd = {}
      if (ersattBefintliga) {
        const avdIFil = [...new Set(unikaMedIds.map(id => allaMedarbetare.find(m => m.id === id)?.avdelning).filter(Boolean))]
        for (const avd of avdIFil) {
          const { data: perRader } = await supabase
            .from('perioder')
            .select('id, startdatum, slutdatum, antal_veckor')
            .eq('avdelning', avd)
            .lte('startdatum', preview.forstaDatum)
            .gte('slutdatum', preview.forstaDatum)
            .limit(1)
          if (!perRader || perRader.length === 0) {
            await visaDialog({
              titel: 'Ingen befintlig period hittades',
              text: `Det finns ingen period för ${avd} som innehåller ${preview.forstaDatum}. Inget har ändrats. Avmarkera "Ersätt befintliga pass" om du vill skapa en ny period.`,
              okText: 'OK'
            })
            setImporterar(false)
            setImportStatus('')
            return
          }
          befintligPeriodPerAvd[avd] = perRader[0]
        }
        const rader = []
        for (const medId of unikaMedIds) {
          const { count } = await supabase.from('pass')
            .select('id', { count: 'exact', head: true })
            .eq('medarbetare_id', medId)
            .gte('datum', preview.forstaDatum)
            .lte('datum', preview.sistaDatum)
          const m = allaMedarbetare.find(x => x.id === medId)
          const nya = allaBatchPass.filter(p => p.medarbetare_id === medId).length
          rader.push(`${m.fornamn} ${m.efternamn}: ${count || 0} befintliga pass ersätts av ${nya} nya (${preview.forstaDatum} – ${preview.sistaDatum})`)
        }
        const ok = await visaDialog({
          titel: 'Ersätt befintliga pass?',
          text: `Pass före ${preview.forstaDatum} och andra personer rörs inte.`,
          rader,
          okText: 'Ersätt pass',
          avbrytText: 'Avbryt'
        })
        if (!ok) {
          setImporterar(false)
          setImportStatus('')
          return
        }
      }

      for (const medId of unikaMedIds) {
        await supabase.from('pass').delete()
          .eq('medarbetare_id', medId)
          .gte('datum', preview.forstaDatum)
          .lte('datum', preview.sistaDatum)
      }
      setImportStatus(`Importerar ${allaBatchPass.length} pass...`)
      const BATCH_SIZE = 100
      const misslyckadeBuntar = []
      for (let i = 0; i < allaBatchPass.length; i += BATCH_SIZE) {
        const batch = allaBatchPass.slice(i, i + BATCH_SIZE)
        const { error } = await supabase.from('pass').insert(batch)
        if (error) {
          console.error('Bunt', i, 'till', i + batch.length, 'misslyckades:', error)
          misslyckadeBuntar.push({ start: i, slut: i + batch.length, fel: error.message })
        }
      }
      if (misslyckadeBuntar.length > 0) {
        const antalTappade = misslyckadeBuntar.reduce((sum, b) => sum + (b.slut - b.start), 0)
        await visaDialog({
          titel: 'Vissa pass kunde inte sparas',
          text: `${antalTappade} pass i ${misslyckadeBuntar.length} bunt(ar) kunde inte sparas. Fel: ${misslyckadeBuntar[0].fel}. Kontrollera schemat noga, vissa medarbetare kan sakna pass.`,
          okText: 'OK'
        })
      }

      setImportStatus('Skapar perioder och beräknar X-dagar...')
      // Kort paus innan X-dagen räknas ut - skydd mot att databasen ännu inte
      // hunnit göra alla precis insatta rader läsbara (kan annars göra att en
      // enstaka persons X-dag räknas fel om ett skift "missas" i avläsningen).
      await new Promise(resolve => setTimeout(resolve, 1500))
      if (!ersattBefintliga) {
        for (const [medId, info] of Object.entries(medarbetareMedX)) {
          await supabase.from('medarbetare').update({ sort_order: info.sort_order }).eq('id', medId)
        }
      }

      // Skapa perioder per unik avdelning bland matchade medarbetare
      const avdelningar = [...new Set(Object.values(medarbetareMedX).map(m => m.avdelning))]
      // Om inga X-dagar finns, hämta avdelning från alla matchade medarbetare
      const alleMatchadeAvd = [...new Set(allaBatchPass.map(p => {
        const med = allaMedarbetare.find(m => m.id === p.medarbetare_id)
        return med?.avdelning
      }).filter(Boolean))]
      const allaAvdelningar = [...new Set([...avdelningar, ...alleMatchadeAvd])]
      
      for (const avd of ersattBefintliga ? [] : allaAvdelningar) {
        // Skapa inte samma period en gång till om den redan finns
        const { data: finnsRedan } = await supabase
          .from('perioder')
          .select('id')
          .eq('avdelning', avd)
          .eq('startdatum', preview.forstaDatum)
          .eq('slutdatum', preview.sistaDatum)
          .limit(1)
        if (finnsRedan && finnsRedan.length > 0) continue
        await supabase.from('perioder').insert({
          namn: `Period ${preview.forstaDatum} – ${preview.sistaDatum}`,
          startdatum: preview.forstaDatum,
          slutdatum: preview.sistaDatum,
          antal_veckor: period,
          total_timmar: period === 8 ? period * 40 : period * 34.5,
          avdelning: avd,
          skapad_av: user?.id
        })
      }

      // Beräkna X/SX timmar per person - BARA inom denna periods datum
      for (const [medId, info] of Object.entries(medarbetareMedX)) {
        // I ersätt-läge gäller HELA den befintliga periodens veckor och datum
        const befPer = ersattBefintliga ? befintligPeriodPerAvd[info.avdelning] : null
        const veckor = befPer ? befPer.antal_veckor : period
        const periodStart = befPer ? befPer.startdatum : preview.forstaDatum
        const periodSlut = befPer ? befPer.slutdatum : preview.sistaDatum
        // 8-veckors schema = alltid 40h/vecka, annars baserat på schema_typ
        const kravTimmar = (veckor === 8 || info.schema_typ === '40h') ? veckor * 40 : veckor * 34.5

        // Förväntat antal pass för just den här personen, utifrån det vi
        // faktiskt just sparade (facit att jämföra avläsningen mot)
        const forvantatAntal = allaBatchPass.filter(p => p.medarbetare_id === medId).length

        // Hämta pass BARA inom denna period - med en enkel om-försök om
        // antalet inte stämmer mot facit (skydd mot fördröjd läsbarhet)
        let periodPass = null
        for (let forsok = 0; forsok < 3; forsok++) {
          const { data } = await supabase
            .from('pass')
            .select('id, arbetstimmar, passtyp_namn, kategori')
            .eq('medarbetare_id', medId)
            .gte('datum', periodStart)
            .lte('datum', periodSlut)
          periodPass = data
          if ((periodPass || []).length >= forvantatAntal) break
          console.warn(`X-dagsberäkning: fick ${(periodPass||[]).length} pass, förväntade ${forvantatAntal} för medarbetare ${medId} - försöker igen...`)
          await new Promise(resolve => setTimeout(resolve, 800))
        }

        const ovrigaTimmar = (periodPass || [])
          .filter(p => {
            const namn = (p.passtyp_namn || '').toUpperCase().trim()
            return !['X','SX'].includes(namn) && namn !== '' && p.kategori !== 'overtid'
          })
          .reduce((sum, p) => sum + (p.arbetstimmar || 0), 0)

        const xTimmar = Math.round((kravTimmar - ovrigaTimmar) * 10) / 10

        // Uppdatera X och SX med deras ID:n
        const xIds = (periodPass || [])
          .filter(p => ['X','SX'].includes((p.passtyp_namn||'').toUpperCase().trim()))
          .map(p => p.id)

        if (xIds.length > 0) {
          await supabase.from('pass').update({ arbetstimmar: xTimmar }).in('id', xIds)
        }
      }

      // Fixa E1/SE1
      await supabase.from('pass').update({ arbetstimmar: 8.5 })
        .in('passtyp_namn', ['E1', 'SE1']).eq('arbetstimmar', 0)

      setImportStatus('')
      setKlar(true)
      setPreview(null)
      await visaDialog({ titel: 'Schema importerat', text: 'Gå till Helhetsschema för att se resultatet.', okText: 'OK' })
    } catch (err) {
      console.error(err)
      await visaDialog({ titel: 'Fel vid import', text: err.message, okText: 'OK' })
    } finally {
      setImporterar(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      {dialog && (
        <div className="modal-overlay" onClick={() => stangDialog(false)}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{dialog.titel}</h3>
            </div>
            <div className="modal-body">
              {dialog.text && <p style={{ fontSize: '.88rem', color: '#444', margin: '0 0 12px' }}>{dialog.text}</p>}
              {dialog.rader && dialog.rader.length > 0 && (
                <div style={{ background: '#f7f7f7', border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 12px', fontSize: '.84rem', color: '#333', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {dialog.rader.map((r, i) => <div key={i}>{r}</div>)}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {dialog.avbrytText && (
                <button className="btn btn-secondary" onClick={() => stangDialog(false)}>{dialog.avbrytText}</button>
              )}
              <button className="btn btn-primary" onClick={() => stangDialog(true)}>{dialog.okText}</button>
            </div>
          </div>
        </div>
      )}
      <div className="content-header">
        <div>
          <h1>Ladda upp schema</h1>
          <div className="content-sub">Importera schema via Excel-fil</div>
        </div>
      </div>

      <div className="content-body" style={{ overflowY: 'auto' }}>
        {klar && (
          <div className="info-box info-box-green" style={{ marginBottom: 16 }}>
             Schema importerat! Gå till Helhetsschema för att se resultatet.
          </div>
        )}

        <div className="card-grid">
          <div className="card">
            <h3> Excel-import</h3>
            <div
              className="upload-zone"
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById('fil-input').click()}
            >
              <div style={{ fontSize: '2rem', marginBottom: 8 }}></div>
              <p style={{ fontSize: '.88rem', fontWeight: 500 }}>Dra och släpp Excel-fil här</p>
              <small style={{ fontSize: '.76rem' }}>eller klicka · .xlsx .xls</small>
              <input id="fil-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFil} />
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="info-box info-box-yellow" style={{ marginBottom: 10 }}>
                 Välj rätt periodlängd innan du importerar. Du kan ändra den både före och efter att du laddat in filen, men se till att rätt värde är valt innan du klickar "Importera schema".
              </div>
              <label className="form-label">Periodlängd</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[8, 9, 10, 11, 12].map(v => (
                  <button
                    key={v}
                    onClick={() => setPeriod(v)}
                    style={{
                      flex: 1, padding: 9, borderRadius: 6,
                      border: `1.5px solid ${period === v ? '#F11C0A' : '#e8e8e8'}`,
                      background: period === v ? '#F11C0A' : '#fff',
                      color: period === v ? '#fff' : '#444',
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: '.78rem'
                    }}
                  >
                    {v} v<br /><small style={{ opacity: .75 }}>{v === 8 ? '320' : v === 9 ? '360' : v === 10 ? '345' : v === 11 ? '379,5' : '414'} h</small>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <h3> Ladda ner mall</h3>
            <p style={{ fontSize: '.83rem', color: '#666', marginBottom: 12, lineHeight: 1.6 }}>
              Välj avdelning, startdatum och antal veckor. Mallen kommer med datum i rad 1 och alla aktiva medarbetare i kolumn A, i samma ordning som i schemat. Fyll bara i passen. Väljer du inget startdatum får du en tom Excel med exempelnamn.
            </p>

            <label className="form-label">Avdelning</label>
            <select className="form-control" value={mallAvd} onChange={e => setMallAvd(e.target.value)}>
              {AVDELNINGAR.map(a => <option key={a} value={a}>{a}</option>)}
            </select>

            <label className="form-label">Startdatum</label>
            <input className="form-control" type="date" value={mallStart} onChange={e => setMallStart(e.target.value)} />

            <label className="form-label">Antal veckor</label>
            <select className="form-control" value={mallVeckor} onChange={e => setMallVeckor(Number(e.target.value))}>
              {[8, 9, 10, 11, 12].map(v => <option key={v} value={v}>{v} veckor</option>)}
            </select>

            {mallFel && <p style={{ margin: '8px 0 0', fontSize: '.78rem', color: '#c62828', fontWeight: 600 }}>{mallFel}</p>}

            <button className="btn btn-secondary btn-sm" onClick={laddaNerMall} disabled={mallHamtar} style={{ marginTop: 10 }}>
              {mallHamtar ? 'Skapar...' : 'Ladda ner Excel-mall'}
            </button>
          </div>
        </div>

        {preview && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="info-box info-box-green">
               {preview.filnamn} – {preview.medarbetare.length} medarbetare · {preview.dagar} dagar
              {preview.forstaDatum && ` · ${preview.forstaDatum} – ${preview.sistaDatum}`}
            </div>

            {preview.okandaKoder && preview.okandaKoder.length > 0 && (
              <div className="info-box info-box-red" style={{ marginBottom: 14 }}>
                <strong> {preview.okandaKoder.length} okända pass importeras INTE</strong> – dessa koder finns inte i Passtyper-listan. Rätta stavningen i Excel-filen eller lägg till koden under Passtyper, ladda sedan upp filen igen.
                <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto', fontSize: '.78rem' }}>
                  {preview.okandaKoder.map((o, idx) => (
                    <div key={idx} style={{ padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                      <strong>{o.kod}</strong> – {o.namn} · {o.datum}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label className="form-label">Hittade passtyper</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {preview.passNamn.map(n => <span key={n} className="tag tag-blue">{n}</span>)}
            </div>

            <label className="form-label">Timmar per medarbetare</label>
            <div style={{ border: '1px solid #ececec', borderRadius: 6, overflow: 'hidden', fontSize: '.82rem', marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 10px', background: '#f8f8f8', fontWeight: 700, borderBottom: '1px solid #ececec' }}>
                <span>Medarbetare</span><span>Timmar</span>
              </div>
              {preview.medarbetare.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 10px', borderBottom: '1px solid #f5f5f5' }}>
                  <span>{m.namn}</span>
                  <span style={{ color: '#888' }}>{m.totalTimmar}h</span>
                </div>
              ))}
            </div>

            {importerar && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', color: '#555', marginBottom: 5 }}>
                  <span>{importStatus || 'Importerar...'}</span>
                </div>
                <div style={{ background: '#e8e8e8', borderRadius: 20, height: 8, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 20, background: '#F11C0A',
                    animation: 'progress-bar 2s ease-in-out infinite',
                    width: '40%'
                  }} />
                </div>
              </div>
            )}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '.85rem', marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={ersattBefintliga} disabled={importerar}
                onChange={e => setErsattBefintliga(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong>Ersätt befintliga pass i befintlig period</strong> (för schemabyte mitt i en period).
                Skapar ingen ny period. Bara personerna i filen och bara filens datum berörs. X-dagen räknas om för hela periodens krav.
              </span>
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPreview(null)} disabled={importerar}>Avbryt</button>
              <button className="btn btn-primary" onClick={importera} disabled={importerar}>
                {importerar ? 'Importerar...' : 'Importera schema'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

